import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  message,
  Typography,
  Grid,
  Select,
  Card,
  Row,
  Col,
  Statistic,
  Tabs,
  Popconfirm,
  Upload,
  Segmented,
  Checkbox,
  Breadcrumb,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  FolderOutlined,
  DownloadOutlined,
  UploadOutlined,
  EyeOutlined,
  SearchOutlined,
  InboxOutlined,
  FolderOpenOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileZipOutlined,
  AudioOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { fileApi, folderApi, ossFileApi, API_BASE_URL } from '@/services/api'
import type { UploadFile, UploadProps } from 'antd/es/upload/interface'

const { Title, Text } = Typography
const { Search } = Input
const { useBreakpoint } = Grid
const { Option } = Select
const { TabPane } = Tabs
const { Dragger } = Upload

interface FileItem {
  id: string          // 数据库自增ID
  file_id: string    // UUID 用于API调用
  name: string
  type: 'file' | 'folder'
  size?: number
  mime_type?: string
  path: string
  file_path: string  // 存储路径
  file_url: string   // 访问URL
  preview_url?: string  // OSS 预览URL（签名URL）
  storage_backend?: string  // 存储后端: oss, minio(历史), local
  created_at: string
  updated_at: string
  download_count: number
  tags: string[]
  description?: string
  is_public?: boolean  // OSS 文件的公开/私密
  md5?: string         // 文件 MD5
  width?: number       // 图片宽度
  height?: number      // 图片高度
  duration?: number    // 视频/音频时长
  is_deleted?: boolean // 是否已删除
}

interface Folder {
  id: string
  name: string
  path: string
  parent_id?: string
  created_at: string
  updated_at: string
  file_count: number
}

const FileManagement: React.FC = () => {
  const [files, setFiles] = useState<FileItem[]>([])
  const [folders, setFolders] = useState<Folder[]>([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [modalVisible, setModalVisible] = useState(false)
  const [uploadModalVisible, setUploadModalVisible] = useState(false)
  const [folderModalVisible, setFolderModalVisible] = useState(false)
  const [editingFile, setEditingFile] = useState<FileItem | null>(null)
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null)
  const [currentFolder, setCurrentFolder] = useState<string | null>(null)
  const [breadcrumbItems, setBreadcrumbItems] = useState<Array<{ title: string; folderId?: string }>>([
    { title: '根目录', folderId: undefined }
  ])
  const [form] = Form.useForm()
  const [folderForm] = Form.useForm()
  const screens = useBreakpoint()
  const [activeTab, setActiveTab] = useState('files')
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 })

  // 存储源 Tab（OSS / 通用文件）
  const [storageSource, setStorageSource] = useState<'oss' | 'local'>('oss')
  // 是否显示已删除文件
  const [showDeleted, setShowDeleted] = useState(false)
  // OSS 上传时的公开/私密选择
  const [ossUploadIsPublic, setOssUploadIsPublic] = useState(false)
  // 图片预览
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewUrl, setPreviewUrl] = useState('')
  // 文件详情弹窗
  const [detailVisible, setDetailVisible] = useState(false)
  const [detailRecord, setDetailRecord] = useState<FileItem | null>(null)

  useEffect(() => {
    fetchFiles()
  }, [searchText, currentFolder, pagination.current, storageSource, showDeleted])

  const fetchFiles = async () => {
    setLoading(true)
    try {
      if (storageSource === 'oss') {
        // OSS 文件（增强版 API）
        const params: any = {
          page: pagination.current,
          limit: pagination.pageSize,
        }
        if (searchText) params.search = searchText
        if (showDeleted) params.include_deleted = true

        const response = await ossFileApi.getFiles(params)
        const items = response.data.items || []
        const total = response.data.total || 0

        const mappedItems: FileItem[] = items.map((item: any) => ({
          id: String(item.id),
          file_id: String(item.id),  // OSS 用 id 作为 file_id
          name: item.file_name || '未命名',
          type: 'file' as const,
          size: item.file_size,
          mime_type: item.file_ext ? getMimeType(item.file_ext) : 'application/octet-stream',
          path: item.file_path,
          file_path: item.file_path,
          file_url: item.file_path,
          preview_url: item.preview_url,  // 增强版 API 返回 preview_url（已签名）
          storage_backend: 'oss',
          is_deleted: item.is_deleted,
          created_at: item.created_at,
          updated_at: item.created_at,
          download_count: 0,
          tags: [],
          md5: item.md5,
          is_public: item.is_public,
        }))
        setFiles(mappedItems)
        setPagination(prev => ({ ...prev, total }))
      } else {
        // 通用文件（当前后端默认也走 OSS）
        const params: any = {
          page: pagination.current,
          page_size: pagination.pageSize,
        }
        if (searchText) params.search = searchText
        if (currentFolder) params.folder_id = currentFolder
        // storage_source 筛选
        params.storage_backend = 'oss'

        const response = await fileApi.getFiles(params)
        const items = response.data.items || []
        const total = response.data.total || 0

        const mappedItems: FileItem[] = items.map((item: any) => ({
          id: String(item.id),
          file_id: item.file_id,
          name: item.original_filename || item.stored_filename || '未命名',
          type: 'file' as const,
          size: item.file_size,
          mime_type: item.mime_type,
          path: item.file_path || item.file_url,
          file_path: item.file_path,
          file_url: item.file_url,
          storage_backend: item.storage_backend,
          created_at: item.created_at,
          updated_at: item.updated_at,
          download_count: item.download_count || 0,
          tags: [],
          description: item.description,
        }))
        setFiles(mappedItems)
        setPagination(prev => ({ ...prev, total }))
      }
    } catch (error) {
      message.error('获取文件列表失败')
    } finally {
      setLoading(false)
    }
  }

  const fetchFolders = async () => {
    try {
      const params: any = { page: 1, page_size: 100 }
      if (currentFolder) {
        params.parent_id = currentFolder
      }
      const response = await folderApi.getFolders(params)
      setFolders(response.data.items || [])
    } catch (error) {
      console.error('Failed to fetch folders:', error)
    }
  }

  const handleCreateFile = async (values: any) => {
    try {
      await fileApi.createFile(values)
      message.success('创建文件成功')
      setModalVisible(false)
      form.resetFields()
      fetchFiles()
    } catch (error) {
      message.error('创建文件失败')
    }
  }

  const handleUpdateFile = async (id: string, values: any) => {
    try {
      await fileApi.updateFile(id, values)
      message.success('更新文件成功')
      setModalVisible(false)
      setEditingFile(null)
      form.resetFields()
      fetchFiles()
    } catch (error) {
      message.error('更新文件失败')
    }
  }

  const handleDeleteFile = async (fileId: string, storageBackend?: string) => {
    try {
      if (storageBackend === 'oss') {
        await ossFileApi.deleteFile(Number(fileId))
      } else {
        await fileApi.deleteFile(fileId)
      }
      message.success('删除文件成功')
      fetchFiles()
    } catch (error) {
      message.error('删除文件失败')
    }
  }

  const handleCreateFolder = async (values: any) => {
    try {
      const folderData = {
        ...values,
        parent_id: currentFolder,
      }
      await folderApi.createFolder(folderData)
      message.success('创建文件夹成功')
      setFolderModalVisible(false)
      folderForm.resetFields()
      fetchFolders()
    } catch (error) {
      message.error('创建文件夹失败')
    }
  }

  const handleDeleteFolder = async (id: string) => {
    try {
      await folderApi.deleteFolder(id)
      message.success('删除文件夹成功')
      fetchFolders()
    } catch (error) {
      message.error('删除文件夹失败')
    }
  }

  const handleFolderClick = (folderId: string | undefined) => {
    setCurrentFolder(folderId || null)

    if (folderId) {
      const folder = folders.find(f => f.id === folderId)
      if (folder) {
        setBreadcrumbItems([...breadcrumbItems, { title: folder.name, folderId }])
      }
    } else {
      setBreadcrumbItems([{ title: '根目录', folderId: undefined }])
    }
  }

  const handleBreadcrumbClick = (index: number) => {
    const newBreadcrumb = breadcrumbItems.slice(0, index + 1)
    setBreadcrumbItems(newBreadcrumb)
    setCurrentFolder(newBreadcrumb[newBreadcrumb.length - 1].folderId)
  }

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    fileList: fileList,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList)
    },
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const formData = new FormData()
        formData.append('file', file as File)

        // 根据当前 Tab 确定上传到哪个存储
        if (storageSource === 'oss') {
          // OSS 上传：使用选择的 is_public 参数
          await ossFileApi.uploadFile(formData, ossUploadIsPublic)
        } else {
          // 通用文件上传（默认走 OSS）
          formData.append('owner_type', 'system')
          formData.append('storage_type', 'oss')
          await fileApi.uploadFile(formData)
        }

        onSuccess?.(null)
        message.success(`${file.name} 上传成功`)
        setFileList([])
        fetchFiles()
      } catch (error) {
        console.error('Upload failed:', error)
        onError?.(error as Error)
        message.error(`${file.name} 上传失败`)
      }
    },
  }

  const getFileIcon = (mimeType?: string) => {
    if (!mimeType) return <FileTextOutlined />

    if (mimeType.startsWith('image/')) return <FileImageOutlined className="text-green-500" />
    if (mimeType.startsWith('video/')) return <VideoCameraOutlined className="text-blue-500" />
    if (mimeType.startsWith('audio/')) return <AudioOutlined className="text-purple-500" />
    if (mimeType.includes('pdf')) return <FilePdfOutlined className="text-red-500" />
    if (mimeType.includes('zip') || mimeType.includes('rar')) return <FileZipOutlined className="text-yellow-500" />

    return <FileTextOutlined className="text-gray-500" />
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A'
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`
  }

  // 根据文件扩展名获取正确的 MIME 类型
  const getMimeType = (fileExt: string): string => {
    const ext = fileExt.toLowerCase().replace('.', '')
    const mimeTypes: Record<string, string> = {
      // 图片
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      bmp: 'image/bmp',
      webp: 'image/webp',
      svg: 'image/svg+xml',
      // 视频
      mp4: 'video/mp4',
      avi: 'video/x-msvideo',
      mov: 'video/quicktime',
      wmv: 'video/x-ms-wmv',
      flv: 'video/x-flv',
      mkv: 'video/x-matroska',
      webm: 'video/webm',
      // 音频
      mp3: 'audio/mpeg',
      wav: 'audio/wav',
      ogg: 'audio/ogg',
      flac: 'audio/flac',
      aac: 'audio/aac',
      m4a: 'audio/mp4',
      // 文档
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ppt: 'application/vnd.ms-powerpoint',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      txt: 'text/plain',
      // 压缩包
      zip: 'application/zip',
      rar: 'application/vnd.rar',
      '7z': 'application/x-7z-compressed',
      tar: 'application/x-tar',
      gz: 'application/gzip',
    }
    return mimeTypes[ext] || 'application/octet-stream'
  }

  // 获取文件的完整预览 URL
  const getPreviewUrl = (record: FileItem): string => {
    // OSS 文件使用 preview_url（已签名的 URL）
    if (record.storage_backend === 'oss') {
      return record.preview_url || record.file_url || record.path || ''
    }
    // 如果是完整 URL，直接返回
    if (record.file_url?.startsWith('http')) {
      return record.file_url
    }
    // 本地/通用存储：/storage 路径直接返回（后端已挂载 /storage 到静态文件）
    // file_url 格式如 /storage/private/2026/03/18/xxx.svg
    if (record.file_url?.startsWith('/storage/')) {
      return record.file_url
    }
    // 其他相对路径需要拼接 API_BASE_URL
    if (record.file_url?.startsWith('/')) {
      return `${API_BASE_URL}${record.file_url}`
    }
    return record.file_url || record.path || ''
  }

  const fileColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: string) => <Text copyable>{id}</Text>
    },
    {
      title: '名称',
      key: 'name',
      render: (_, record: FileItem) => (
        <Space>
          {record.type === 'folder' ? (
            <FolderOutlined className="text-blue-500" />
          ) : (
            getFileIcon(record.mime_type)
          )}
          <div>
            <div
              className="font-medium cursor-pointer text-blue-500 hover:underline"
              onClick={() => {
                if (record.type === 'folder') {
                  handleFolderClick(record.id)
                }
              }}
            >
              {record.name}
            </div>
            {record.description && (
              <div className="text-xs text-gray-500">{record.description}</div>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: '大小',
      key: 'size',
      render: (_, record: FileItem) => {
        if (record.type === 'folder') {
          return <Text type="secondary">文件夹</Text>
        }
        return formatFileSize(record.size)
      },
    },
    {
      title: '类型',
      dataIndex: 'mime_type',
      key: 'mime_type',
      render: (mimeType?: string) => {
        if (!mimeType) return <Text type="secondary">未知</Text>
        return <Tag>{mimeType.split('/')[1]?.toUpperCase()}</Tag>
      },
    },
    {
      title: '存储',
      dataIndex: 'storage_backend',
      key: 'storage_backend',
      width: 80,
      render: (backend?: string) => {
        if (backend === 'oss') return <Tag color="orange">OSS</Tag>
        if (backend === 'minio') return <Tag color="blue">本地</Tag>
        if (backend === 'local') return <Tag color="green">Local</Tag>
        return <Text type="secondary">-</Text>
      },
    },
    {
      title: '可见性',
      key: 'visibility',
      width: 80,
      render: (_, record: FileItem) => {
        // 只有 OSS 文件有可见性
        if (record.storage_backend !== 'oss') return <Text type="secondary">-</Text>
        return record.is_public
          ? <Tag color="green">公开</Tag>
          : <Tag color="gold">私密</Tag>
      },
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags?: string[]) => (
        <Space wrap>
          {(tags || []).map((tag: string) => (
            <Tag key={tag} color="blue">
              {tag}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '下载次数',
      dataIndex: 'download_count',
      key: 'download_count',
      render: (count?: number) => (count ?? 0).toLocaleString(),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date?: string) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, record: FileItem) => (
        <Space>
          <Button
            type="text"
            size="small"
            onClick={() => {
              setDetailRecord(record)
              setDetailVisible(true)
            }}
          >
            详情
          </Button>
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => {
              const previewUrl = getPreviewUrl(record)
              if (!previewUrl) return
              // 图片类型显示预览弹窗
              if (record.mime_type?.startsWith('image/')) {
                setPreviewUrl(previewUrl)
                setPreviewVisible(true)
              } else {
                // 非图片类型直接下载
                window.open(previewUrl, '_blank')
              }
            }}
          >
            查看
          </Button>
          <Popconfirm
            title={`确定要删除这个${record.type === 'folder' ? '文件夹' : '文件'}吗？`}
            onConfirm={() => {
              if (record.type === 'folder') {
                handleDeleteFolder(record.id)
              } else {
                handleDeleteFile(record.file_id, record.storage_backend)
              }
            }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small">
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const allItems = [...folders, ...files]

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>文件管理</Title>
        <Text type="secondary">管理剧本、文档和多媒体文件</Text>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="文件列表" key="files">
          <Card>
            <div className="mb-4">
              <Breadcrumb>
                {breadcrumbItems.map((item, index) => (
                  <Breadcrumb.Item
                    key={index}
                    className="cursor-pointer"
                    onClick={() => handleBreadcrumbClick(index)}
                  >
                    {item.title}
                  </Breadcrumb.Item>
                ))}
              </Breadcrumb>
            </div>

            <div className="mb-4 flex justify-between items-center">
              <Space>
                <Search
                  placeholder="搜索文件"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 300 }}
                  allowClear
                  prefix={<SearchOutlined />}
                />
                <Segmented
                  value={storageSource}
                  onChange={(value) => setStorageSource(value as 'oss' | 'local')}
                  options={[
                    { label: 'OSS 文件', value: 'oss' },
                    { label: '通用文件', value: 'local' },
                  ]}
                />
                {storageSource === 'oss' && (
                  <Checkbox
                    checked={showDeleted}
                    onChange={(e) => setShowDeleted(e.target.checked)}
                  >
                    显示已删除
                  </Checkbox>
                )}
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => setUploadModalVisible(true)}
                >
                  上传
                </Button>
              </Space>
            </div>

            <Table
              columns={fileColumns}
              dataSource={allItems}
              rowKey="id"
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
                onChange: (page, pageSize) => {
                  setPagination(prev => ({ current: page, pageSize: pageSize || 20, total: prev.total }))
                },
              }}
            />
          </Card>
        </TabPane>

        <TabPane tab="统计" key="stats">
          <Card>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="总文件数"
                  value={files.filter(f => f.type === 'file').length}
                  prefix={<FileTextOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="文件夹数"
                  value={folders.length}
                  prefix={<FolderOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="总存储空间"
                  value={files
                    .filter(f => f.type === 'file')
                    .reduce((sum, f) => sum + (f.size || 0), 0)}
                  formatter={(value) => formatFileSize(Number(value))}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="总下载次数"
                  value={files.reduce((sum, f) => sum + f.download_count, 0)}
                  prefix={<DownloadOutlined />}
                />
              </Col>
            </Row>
          </Card>
        </TabPane>
      </Tabs>

      {/* 创建/编辑文件模态框 */}
      <Modal
        title={
          <span>
            {editingFile ? '编辑文件' : '创建文件'}
            {editingFile && (
              <Text type="secondary" className="ml-3 text-sm">
                ID: {editingFile.id}
              </Text>
            )}
          </span>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false)
          setEditingFile(null)
          form.resetFields()
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            if (editingFile) {
              handleUpdateFile(editingFile.file_id, values)
            } else {
              handleCreateFile(values)
            }
          }}
        >
          <Form.Item
            name="name"
            label="文件名"
            rules={[{ required: true, message: '请输入文件名' }]}
          >
            <Input placeholder="请输入文件名" />
          </Form.Item>

          <Form.Item name="description" label="描述">
            <Input.TextArea placeholder="请输入文件描述" rows={3} />
          </Form.Item>

          <Form.Item name="tags" label="标签">
            <Select mode="tags" placeholder="请输入标签后按回车">
              <Option value="剧本">剧本</Option>
              <Option value="文档">文档</Option>
              <Option value="图片">图片</Option>
              <Option value="音频">音频</Option>
              <Option value="视频">视频</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space className="w-full justify-end">
              <Button
                onClick={() => {
                  setModalVisible(false)
                  setEditingFile(null)
                  form.resetFields()
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingFile ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 上传文件模态框 */}
      <Modal
        title="上传文件"
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false)
          setFileList([])
        }}
        footer={null}
        width={600}
      >
        <div className="mb-4 text-gray-500">
          当前上传到：<Tag color={storageSource === 'oss' ? 'orange' : 'blue'}>
            {storageSource === 'oss' ? '阿里云 OSS' : '通用文件 (本地)'}
          </Tag>
        </div>

        {storageSource === 'oss' ? (
          // OSS 模式：两个上传按钮（公开/私密）
          <Space direction="vertical" className="w-full" size="large">
            <Dragger
              {...uploadProps}
              onChange={({ fileList: newFileList }) => {
                setFileList(newFileList)
                setOssUploadIsPublic(true)  // 公开上传
              }}
              customRequest={async ({ file, onSuccess, onError }) => {
                try {
                  const formData = new FormData()
                  formData.append('file', file as File)
                  await ossFileApi.uploadFile(formData, true)  // is_public = true
                  onSuccess?.(null)
                  message.success(`${file.name} 公开上传成功`)
                  setFileList([])
                  fetchFiles()
                } catch (error) {
                  console.error('Upload failed:', error)
                  onError?.(error as Error)
                  message.error(`${file.name} 上传失败`)
                }
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽上传公开文件</p>
              <p className="ant-upload-hint">
                公开文件可匿名访问，适用于图片、文档等公共资源
              </p>
            </Dragger>

            <Dragger
              {...uploadProps}
              onChange={({ fileList: newFileList }) => {
                setFileList(newFileList)
                setOssUploadIsPublic(false)  // 私密上传
              }}
              customRequest={async ({ file, onSuccess, onError }) => {
                try {
                  const formData = new FormData()
                  formData.append('file', file as File)
                  await ossFileApi.uploadFile(formData, false)  // is_public = false
                  onSuccess?.(null)
                  message.success(`${file.name} 私密上传成功`)
                  setFileList([])
                  fetchFiles()
                } catch (error) {
                  console.error('Upload failed:', error)
                  onError?.(error as Error)
                  message.error(`${file.name} 上传失败`)
                }
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽上传私密文件</p>
              <p className="ant-upload-hint">
                私密文件需要签名 URL 访问，适用于敏感文件
              </p>
            </Dragger>
          </Space>
        ) : (
          // 通用文件模式：单个上传按钮
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">
              支持单个或批量上传。不要上传敏感或违反法律的文件。
            </p>
          </Dragger>
        )}
      </Modal>

      {/* 图片预览弹窗 */}
      <Modal
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={800}
        centered
      >
        <img
          alt="预览"
          style={{ width: '100%' }}
          src={previewUrl}
        />
      </Modal>

      {/* 文件详情弹窗 */}
      <Modal
        title="文件详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={500}
      >
        {detailRecord && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Text strong>文件名：</Text>
              <Text>{detailRecord.name}</Text>
            </div>
            <div className="flex items-center gap-2">
              <Text strong>文件大小：</Text>
              <Text>{formatFileSize(detailRecord.size)}</Text>
            </div>
            <div className="flex items-center gap-2">
              <Text strong>文件类型：</Text>
              <Tag>{detailRecord.mime_type}</Tag>
            </div>
            <div className="flex items-center gap-2">
              <Text strong>存储位置：</Text>
              {detailRecord.storage_backend === 'oss' && <Tag color="orange">阿里云 OSS</Tag>}
              {detailRecord.storage_backend === 'minio' && <Tag color="blue">本地</Tag>}
              {detailRecord.storage_backend === 'local' && <Tag color="green">本地存储</Tag>}
            </div>
            {detailRecord.storage_backend === 'oss' && (
              <div className="flex items-center gap-2">
                <Text strong>可见性：</Text>
                {detailRecord.is_public
                  ? <Tag color="green">公开</Tag>
                  : <Tag color="gold">私密</Tag>
                }
              </div>
            )}
            {detailRecord.md5 && (
              <div className="flex items-center gap-2">
                <Text strong>MD5：</Text>
                <Text code copyable>{detailRecord.md5}</Text>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Text strong>文件路径：</Text>
              <Text code copyable>{detailRecord.path || detailRecord.file_path}</Text>
            </div>
            <div className="flex items-center gap-2">
              <Text strong>访问地址：</Text>
              <Text code copyable>{getPreviewUrl(detailRecord) || '-'}</Text>
            </div>
            <div className="flex items-center gap-2">
              <Text strong>创建时间：</Text>
              <Text>{detailRecord.created_at ? new Date(detailRecord.created_at).toLocaleString() : '-'}</Text>
            </div>
            <div className="flex items-center gap-2">
              <Text strong>更新时间：</Text>
              <Text>{detailRecord.updated_at ? new Date(detailRecord.updated_at).toLocaleString() : '-'}</Text>
            </div>
            {detailRecord.storage_backend === 'oss' && (
              <div className="flex items-center gap-2">
                <Text strong>预览地址：</Text>
                <Text code copyable ellipsis style={{ maxWidth: 300 }}>{detailRecord.preview_url || '-'}</Text>
              </div>
            )}
            {getPreviewUrl(detailRecord) && (
              <div>
                <Button
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    const url = getPreviewUrl(detailRecord)
                    if (detailRecord.mime_type?.startsWith('image/')) {
                      setPreviewUrl(url)
                      setPreviewVisible(true)
                    } else {
                      window.open(url, '_blank')
                    }
                  }}
                >
                  查看文件
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 创建文件夹模态框 */}
      <Modal
        title="创建文件夹"
        open={folderModalVisible}
        onCancel={() => {
          setFolderModalVisible(false)
          setEditingFolder(null)
          folderForm.resetFields()
        }}
        footer={null}
        width={400}
      >
        <Form
          form={folderForm}
          layout="vertical"
          onFinish={handleCreateFolder}
        >
          <Form.Item
            name="name"
            label="文件夹名称"
            rules={[{ required: true, message: '请输入文件夹名称' }]}
          >
            <Input placeholder="请输入文件夹名称" />
          </Form.Item>

          <Form.Item>
            <Space className="w-full justify-end">
              <Button
                onClick={() => {
                  setFolderModalVisible(false)
                  setEditingFolder(null)
                  folderForm.resetFields()
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                创建
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default FileManagement
